import type { OnChanges, SimpleChanges } from '@angular/core';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { first } from 'rxjs/operators';
import { DialogService } from '../../dialogs';
import type { ButtonDef, ButtonDefWithValue, CommandV2, GamepadControl } from '../../models';
import { ButtonType, DEFAULT_MODIFIER_THRESHOLD, defaultModifierThresholds, GamepadButtonAction } from '../../models';
import { GamepadButtonActionDialogComponent } from '../gamepad-button-action-dialog';
import {
  findDuplicateBindings,
  findModifierSourceOverlaps,
  findUnreachableModifierActions,
} from '../services/gamepad-modifier-resolver';

@Component({
  selector: 'app-gamepad-button-action',
  templateUrl: './gamepad-button-action.component.html',
  styleUrls: ['../shared-styles.scss', './gamepad-button-action.component.scss'],
})
export class GamepadButtonActionComponent implements OnChanges {
  @Input()
  actions: GamepadButtonAction[] = [];

  @Input()
  pressedValues: ButtonDefWithValue[] = [];

  @Input()
  controls: GamepadControl[];

  @Input()
  projectId: string;

  @Input()
  commands: CommandV2[];

  @Input()
  sources: ButtonDef[] = [];

  /** So the Hold column lights at the same point the runtime switches layer. */
  @Input()
  modifierThresholds: { [buttonType: number]: number } = defaultModifierThresholds();

  @Output()
  actionsChange = new EventEmitter<GamepadButtonAction[]>();

  @ViewChild('actionsTable')
  actionsTable: MatTable<GamepadButtonAction>;

  public pressedButtonsObject: {
    [name: string]: ButtonDefWithValue;
  } = {};

  get json(): any {
    if (this.actions) {
      return this.actions.map((item) => {
        return item.toJSON();
      });
    }
  }

  dialogRef: MatDialogRef<GamepadButtonActionDialogComponent>;

  displayedColumns: string[] = ['name', 'source', 'modifiers', 'frequency', 'type', 'commandId', 'editing'];

  /** Never a reason to block a save. */
  modifierAdvisories: string[] = [];

  constructor(private dialog: MatDialog, private dialogService: DialogService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['commands'] && this.dialogRef?.componentInstance) {
      // Update commands to current opening dialog if there are any
      this.dialogRef.componentInstance.setControls(this.controls);
    }
    if (changes['sources'] && this.dialogRef?.componentInstance) {
      // Update sources to current opening dialog if there are any
      this.dialogRef.componentInstance.setSources(this.sources);
    }

    if (changes['actions'] || changes['sources']) {
      this.modifierAdvisories = this.buildModifierAdvisories();
    }

    // Only when a frame actually arrives: reassigning unconditionally blanks the Source and Hold
    // columns on every other input change.
    if (changes['pressedValues']) {
      const obj = {};
      this.pressedValues?.forEach((value) => {
        obj[value.type] = value;
      });
      this.pressedButtonsObject = obj;
    }
  }

  /**
   * Compares against the profile's own thresholds, not a constant, so the preview tracks the runtime.
   * No hysteresis, so it can flicker between release and engage — cosmetic, not worth frame history.
   */
  isModifierHeld(action: GamepadButtonAction): boolean {
    if (!action?.modifiers?.length) {
      return false;
    }

    return action.modifiers.every((modifier) => {
      const reading = this.pressedButtonsObject[modifier.type];
      const threshold = this.modifierThresholds?.[modifier.type] ?? DEFAULT_MODIFIER_THRESHOLD;

      return Math.abs(reading?.value ?? 0) >= threshold;
    });
  }

  /** The inputs an action waits on, for the table's Hold column. */
  modifierLabel(action: GamepadButtonAction): string {
    if (!action?.modifiers?.length) {
      return '—';
    }

    return action.modifiers.map((modifier) => modifier.text || ButtonType[modifier.type]).join(' + ');
  }

  onEdit(item: GamepadButtonAction, index: number) {
    const dialogRef = this.dialog.open(GamepadButtonActionDialogComponent, {
      data: {
        projectId: this.projectId,
        model: item,
        controls: this.controls,
        commandsV2: this.commands,
        sources: this.sources,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const model = GamepadButtonAction.fromJSON(result);
        if (this.actions) {
          this.actions.splice(index, 1, model);

          this.actionsTable.renderRows();

          this.actionsChanged();
        }
      }
    });
  }

  onRemove(item: GamepadButtonAction, index: number) {
    const title = 'Delete Action?';
    const message = '<p>Are you sure you want to delete this action?</p>';

    this.dialogService
      .deleteConfirm(title, message)
      .pipe(first())
      .subscribe((confirmed) => {
        if (confirmed) {
          this.actions.splice(index, 1);

          this.actionsTable.renderRows();

          this.actionsChanged();
        }
      });
  }

  onAddNew() {
    this.dialogRef = this.dialog.open(GamepadButtonActionDialogComponent, {
      data: {
        projectId: this.projectId,
        commandsV2: this.commands,
        controls: this.controls,
        sources: this.sources,
      },
    });

    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const newAction = GamepadButtonAction.fromJSON(result);

        this.actions = [...this.actions, newAction];

        this.actionsChanged();
      }
    });
  }

  private buildModifierAdvisories(): string[] {
    const advisories: string[] = [];

    // An empty source list means the mapping has not loaded, not that every modifier is unreachable.
    if (this.sources?.length) {
      const availableTypes = new Set(this.sources.map((source) => source.type));

      findUnreachableModifierActions(this.actions, availableTypes).forEach(({ action, missing }) => {
        const names = missing.map((type) => ButtonType[type]).join(', ');
        advisories.push(`"${action.name}" can never fire: this controller does not report ${names}.`);
      });
    }

    findModifierSourceOverlaps(this.actions).forEach((action) => {
      advisories.push(
        `"${action.name}" fires from ${action.source?.text}, which another action uses as a hold — ` +
          `holding it will do both.`,
      );
    });

    findDuplicateBindings(this.actions).forEach((action) => {
      advisories.push(`"${action.name}" shares its source and hold with an earlier action, so both fire together.`);
    });

    return advisories;
  }

  private _loadDemoData() {
    const json = [
      {
        name: 'forwardBack',
        source: {
          type: 5,
        },
        frequency: 200,
        triggerType: 'continuously',
        commandId: 'drive',
      },
      {
        name: 'fire',
        source: {
          type: 17,
        },
        frequency: 1000,
        triggerType: 'once',
        commandId: 'forward',
      },
      {
        name: 'stop',
        source: {
          type: 16,
        },
        frequency: 1000,
        triggerType: 'once',
        commandId: 'stop',
      },
    ];
    const actions: GamepadButtonAction[] = GamepadButtonAction.fromJSONToList(json);

    this.actions = actions;
    this.actionsChanged();
  }

  private actionsChanged() {
    // onEdit and onRemove splice in place, so the `actions` binding keeps its identity and ngOnChanges
    // never fires — advisories would go stale on the main authoring path.
    this.modifierAdvisories = this.buildModifierAdvisories();
    this.actionsChange.next(this.actions);
  }
}
